/* eslint-disable @typescript-eslint/member-ordering */
import produce from 'immer';
import { combineLatest, Observable } from 'rxjs';
import { filter, first, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { createEffect, ofType } from '@mode/legacy/util-domain';
import { withLatestSnapshot } from '~/lib/charts/chart-pipes';

import {
  deepCopy,
  DEFAULT_CHART_FORMAT,
  FLAMINGO_RESERVED_REFS,
  getDefaultFieldFormat,
  needsFlatTableFieldFormatBackfill,
  needsFlatTableFormatBlobBackfill,
} from '@mode/shared/util-js';

import {
  backfillFlatTableFieldFormat,
  backfillFlatTableVizFormat,
  ChartActions,
  fromChartSelectors,
  generateColorMapping,
  getUpdatedTableFormat,
  isQuickChartColorChanged,
  requiresFormatUpdateFromEncodingChange,
  TempActions,
} from '@mode/charts/domain';
import {
  ChartDrillDown,
  ChartFormatHelpers,
  ChartTranslator,
  EncodingHelpers,
  FieldFormatHelpers,
  generateSeriesColorMapping,
  getColorMapKey,
  SharedAxisHelpers,
  SwitchRenderModel,
  SwitchSchemaModel,
  updateChartFieldColorMap,
} from '@mode/charts/util-vegas';

import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { Switch } from '@mode/shared/util-switch';
import { FlamingoActions } from '@mode/switch-views/contract';
import { FlamingoFacade } from '@mode/switch-views/contract';
import { fromFlamingoSelectors } from '@mode/switch-views/domain';
import { DataViewFacade } from '@mode/switch-views/contract';

import { ToastService } from '@mode/shared/util-toast';
import { AppStore } from '~/types/main.types';

import { Actions } from '~/actions';
import { ExplorerActionType } from '~/actions/chart-designer/explorer.actions';

import { ChartSelectors } from '~/selectors/chart.selectors';

import { AccountFacade } from '@mode/accounts/contract';
import { peekRedoStack, peekUndoStack } from '@mode/charts/domain';
import { DeprecatedApiService } from '@mode/legacy/data-access-model-js';
import { BugsnagService } from '@mode/shared/util-error-handling';
import { flamingoBackfillOnTheFlyColoring, updateChartLevelColoring } from '~/lib/chart-coloring-helpers';
import { getDefaultColorPaletteForType, isColorPaletteCompatible } from '~/lib/chart-helpers';
import * as EffectHelpers from './effect-helper';

export class MismatchedApiError extends Error {
  name: string;

  constructor(message: string) {
    super(message);
    this.name = 'MismatchedApiError';
    Object.setPrototypeOf(this, MismatchedApiError.prototype);
  }
}

// Effects that populate common data
export class ChartEffects {
  static $inject = [
    'RxStoreService',
    'ApiService',
    'ToastService',
    'DataViewFacade',
    'AccountFacade',
    'FlamingoFacade',
    'actions$',
    'BugsnagService',
    '$q',
  ];

  constructor(
    private store: AppStore,
    private api: DeprecatedApiService,
    private alertService: ToastService,
    private dataViewFacade: DataViewFacade,
    private accountFacade: AccountFacade,
    private flamingoFacade: FlamingoFacade,
    private actions$: Observable<Actions>,
    private bugsnag: BugsnagService,
    private $q: angular.IQService
  ) {}

  private saveChartWithSnapshot(chart: ChartTypes.ChartSnapshot, resource: ChartTypes.ChartResource) {
    return this.updateChart(resource, {
      'chart.view_vegas': JSON.stringify({
        format: chart.format,
        fieldFormats: chart.fieldFormats,
        fieldColors: chart.fieldColors,
        facetSizes: chart.facetSizes,
        title: chart.title,
        description: chart.description,
        caption: chart.caption,
        chartType: chart.chartType,
        encoding: chart.encoding,
        isAutoUpdateDisabled: chart.isAutoUpdateDisabled ?? false,
      }),
      'chart.view': JSON.stringify(resource.view), // Don't blow away the v1View, if it exists
      'chart.color_palette_token': chart.colorPaletteToken,
    });
  }

  private updateChart(resource: ChartTypes.ChartResource, params: any) {
    // TODO: This extra validation should only need to be a temporary check and can be removed
    // after the editor refactor release is out and stable for a bit of time.
    // It accounts for the possibility of the new switch view based forms being returned if the editor
    // flag is enabled during an active browser session. This catches that case and throws an error.
    const editForm = resource.forms('edit');
    // In the legacy flow, the inputs on the edit form should _never_ be keyed
    // by visualization, this represents an error case where the wrong form will be used.
    if (editForm._inputs?.hasOwnProperty('visualization')) {
      const mismatchedApiError = new MismatchedApiError('Chart form expected, but visualization form received.');
      this.bugsnag.notify(mismatchedApiError, (event: any) => {
        event.groupingHash = 'MismatchedApiError: EditorRefactor';
        event.addMetadata('debugInfo', {
          params: JSON.stringify(params),
          resource: JSON.stringify(resource),
        });
      });

      // Return the error and allow it to bubble up into the failure effect.
      return this.$q.reject(mismatchedApiError);
    }

    return this.api.edit(resource, params);
  }

  backfillTableFormats(
    filledFormats: { token: string; filledFormat: ChartTypes.ChartFormat }[],
    filledFieldFormatMaps: { token: string; filledFieldFormats: ChartTypes.FieldFormatMap }[]
  ) {
    return ChartActions.backfillTableFormats({
      filledFormats,
      filledFieldFormatMaps,
    });
  }

  generateFilledTableFormats(action, charts, switchModel: FlamingoTypes.ModeModel) {
    const chartsByQuery = charts.filter((chart) => chart.queryToken === action.resource.query_token);

    // Generate `format.flatTable` backfills when necessary
    const filledTableFormats = chartsByQuery
      .filter((chart) => chart.version === 2 && needsFlatTableFormatBlobBackfill(chart))
      .map((chart) => {
        const filledFormat = backfillFlatTableVizFormat(chart, switchModel, action.view);
        return { token: chart.token, filledFormat: filledFormat };
      });

    return filledTableFormats;
  }

  generateFilledFieldFormats(action, charts, switchModel: FlamingoTypes.ModeModel) {
    const chartsByQuery = charts.filter((chart) => chart.queryToken === action.resource.query_token);

    // Generate `fieldFormats` backfills when necessary
    const filledFieldFormats = chartsByQuery
      .filter((chart) => chart.version === 2 && needsFlatTableFieldFormatBackfill(chart))
      .map((chart) => {
        const filledFieldFormat = backfillFlatTableFieldFormat(chart, switchModel, action.view);
        return { token: chart.token, filledFieldFormats: filledFieldFormat };
      });

    return filledFieldFormats;
  }

  // Select Chart
  exploreSelectChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ExplorerActionType.ExploreChart,
        TempActions.Type.ExploreChartMark,
        TempActions.Type.ExploreChartWithData,
        ExplorerActionType.ExploreChartOnLoad
      ),
      map((action) =>
        ChartActions.selectChart({
          token: FLAMINGO_RESERVED_REFS.ChartExplorer,
        })
      )
    )
  );

  // Update chart type
  updateType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateType),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.accountFacade.reportOwnerColorPalettes$,
        this.dataViewFacade.model$,
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs),
        ({ action, token, queryToken, snapshot }, palettes, model, planRefs) => ({
          action,
          token,
          snapshot,
          palettes,
          queryToken,
          model,
          planRefs,
        })
      ),
      switchMap(({ action, token, snapshot, palettes, queryToken, model, planRefs }) => {
        return this.store.select(fromChartSelectors.createRenderDataModelSelector(token, planRefs[token])).pipe(
          first(),
          map((renderModel) => ({
            action,
            token,
            snapshot,
            palettes,
            queryToken,
            model,
            renderModel,
          }))
        );
      }),
      map(({ action, token, snapshot, palettes, queryToken, model, renderModel }) => {
        let format = snapshot.format;
        let encoding = snapshot.encoding;
        const paletteCompatible = isColorPaletteCompatible(
          action.chartType,
          palettes.find((p) => p.token === snapshot.colorPaletteToken)
        );

        format = {
          ...format,
          useMuzeRenderer: ChartFormatHelpers.isHighChartTypeEnabledForMuze(action.chartType),
        };
        // if the palette is incompatible, reset the color map
        const colorPalette = getDefaultColorPaletteForType(action.chartType, palettes);
        if (!paletteCompatible) {
          format = produce(format, (draft) => {
            draft.colors = {
              theme: '',
              map: {}, // blank because if it's impossible to get a valid chart after an update type in this case
              custom: colorPalette?.value.split(','),
            };
          });
          snapshot.colorPaletteToken = colorPalette?.token;
        }

        // make encoding changes
        const schemaModel = SwitchSchemaModel.make(model, queryToken);
        encoding = EncodingHelpers.convertEncoding(
          snapshot.chartType,
          action.chartType,
          snapshot.encoding,
          schemaModel,
          format
        ).encoding;

        const isSnapshotTypeMuze = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format);
        const isNewTypeMuze = ChartFormatHelpers.useMuzeRenderer(action.chartType, snapshot.format);
        //
        // Convert formats and colors.
        // If converting (Muze QC | VizExplorer) -> (Muze QC | VizExplorer): keep the current formats and colors
        // If converting HighCharts QC -> (Muze QC | VizExplorer): convert formats and colors
        // If converting to PTQC: clear the header formats title and set to show false
        // If converting VizExplorer -> QC: clear the field formats and colors since we clear
        // the entire encoding.
        //
        const muzeToMuze = isSnapshotTypeMuze && isNewTypeMuze;
        const highChartsToMuze = !isSnapshotTypeMuze && isNewTypeMuze;
        const newChartIsPTQC = action?.chartType === 'vegasPivotTable';
        let fieldFormats = {};
        let fieldColors = {};

        if (muzeToMuze) {
          fieldFormats = snapshot.fieldFormats;
          fieldColors = snapshot.fieldColors;
        }

        if (highChartsToMuze) {
          const colorDomain = renderModel ? renderModel.getColorDomain(snapshot.encoding, snapshot.format) : [];
          fieldFormats = ChartFormatHelpers.convertFormats(schemaModel, encoding, format, snapshot.chartType);
          fieldColors = ChartFormatHelpers.convertColors(
            schemaModel,
            encoding,
            format,
            snapshot,
            palettes,
            colorDomain
          );
        }

        if (newChartIsPTQC) {
          fieldFormats = { ...fieldFormats, ...ChartFormatHelpers.hideMeasureNamesHeader(encoding) };
          fieldColors = ChartFormatHelpers.generateDefaultSequentialColor('@CON_$VALUES', colorPalette);
        }

        const newFormat = produce(format, (draft) => {
          if (isNewTypeMuze) {
            draft.colors.map = {};
          }
        });
        // Updating the chart type may require resetting the color palette
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            chartType: action.chartType,
            encoding,
            format: newFormat,
            fieldFormats,
            fieldColors,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateType.type],
        });
      })
    )
  );

  // Update chart title
  updateTitle$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateTitle),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) =>
        ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            title: action.title,
          },
          description: 'update title',
        })
      )
    )
  );

  // Update chart description
  updateDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateDescription),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) =>
        ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            description: action.description,
          },
          description: 'update description',
        })
      )
    )
  );

  // Update chart details (currently both title and description together)
  updateDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateDetails),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) =>
        ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            title: action.title,
            description: action.description,
          },
          description: 'update details',
        })
      )
    )
  );

  // Update chart encoding
  updateEncoding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateEncoding, ChartActions.updateEncodingAndFormat),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.dataViewFacade.model$,
        this.accountFacade.reportOwnerColorPalettes$,
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs)
      ),
      filter(([{ snapshot }, model, palettes, planRefs]) => snapshot.chartType !== 'table'), // defer to backfill version
      switchMap(([{ action, token, snapshot, queryToken }, model, palettes, planRefs]) => {
        return this.store.select(fromChartSelectors.createRenderDataModelSelector(token, planRefs[token])).pipe(
          first(),
          map((renderModel) => ({
            action,
            token,
            snapshot,
            queryToken,
            model,
            palettes,
            renderModel,
          }))
        );
      }),

      map(({ action, token, snapshot, queryToken, model, palettes, renderModel }) => {
        let format = { ...snapshot.format, ...(action.format ?? {}) };
        let encoding = action.encoding;
        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, format);
        let fieldFormats =
          useMuzeRenderer || snapshot.chartType === 'bigValue' ? action.fieldFormats ?? snapshot.fieldFormats : {};

        const rendererChanged =
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format) !== useMuzeRenderer;
        let fieldColors = useMuzeRenderer ? action.fieldColors ?? snapshot.fieldColors : {};
        let colorPaletteToken = action.colorPaletteToken ?? snapshot.colorPaletteToken;

        const needsEncodingTranslation =
          !ChartTypes.isVegasChartType(snapshot.chartType) && (useMuzeRenderer || rendererChanged);
        if (needsEncodingTranslation) {
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Unable to translate encoding.Schema model was not defined');
          }
          if (useMuzeRenderer) {
            encoding = ChartTranslator.translateHighChartsToMuze(snapshot.chartType, encoding, format, schemaModel);
            fieldFormats = ChartFormatHelpers.convertFormats(schemaModel, encoding, format, snapshot.chartType);
            if (rendererChanged) {
              // When the renderer changes from HighCharts -> Muze, we need to populate the latest fieldColors
              const colorDomain = renderModel ? renderModel.getColorDomain(snapshot.encoding, snapshot.format) : [];
              fieldColors = ChartFormatHelpers.convertColors(
                schemaModel,
                encoding,
                format,
                snapshot,
                palettes,
                colorDomain
              );
              format = {
                ...format,
                colors: {
                  ...format.colors,
                  map: {},
                },
              };
            }
          } else {
            encoding = ChartTranslator.translateMuzeToHighCharts(snapshot.chartType, encoding, format, schemaModel);
            if (rendererChanged) {
              // When the renderer changes to HighCharts we need to populate the format color map.
              const { colors, colorPaletteToken: paletteToken } = ChartFormatHelpers.convertFieldColorsToFormats(
                schemaModel,
                encoding,
                snapshot.fieldColors,
                format,
                format.colors.custom
              );
              format = {
                ...format,
                colors: {
                  ...format.colors,
                  ...colors,
                },
              };
              colorPaletteToken = paletteToken ?? colorPaletteToken;
              fieldColors = {};
            }
          }
        }
        // Unlike fields, if a group gets removed, it is never coming back. Creating a new group will
        // generate a new group id. To prevent orphaned groups in the FieldFormatMap, we will clean
        // up any group that has been removed with the encoding change.
        const removedGroups = SharedAxisHelpers.getRemovedGroups(snapshot.encoding, action.encoding);
        const cleanedFieldFormats = produce(fieldFormats, (draft) => {
          removedGroups.forEach((group) => {
            delete draft[FieldFormatHelpers.getFormatMapKeyByGroup(group)];
          });
        });
        // We can clear the QuickCharts color map for vegas chart or when the color field in QuickCharts changes.
        const newFormat = produce(format, (draft) => {
          if (useMuzeRenderer || isQuickChartColorChanged(snapshot.encoding, action.encoding)) {
            draft.colors.map = {};
          }
        });
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            format: newFormat,
            fieldFormats: cleanedFieldFormats,
            fieldColors,
            facetSizes: action.facetSizes ?? snapshot.facetSizes,
            colorPaletteToken,
          },
          description: EffectHelpers.actionToDescriptionMap[action.type],
        });
      })
    )
  );

  // Backfill table on update encoding
  updateTableEncoding$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateEncoding),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      filter(([{ snapshot }]) => snapshot.chartType === 'table'),
      map(([{ action, token, snapshot, queryToken }, model]) => {
        if (requiresFormatUpdateFromEncodingChange(snapshot.encoding, action.encoding)) {
          snapshot = {
            ...snapshot,
            ...getUpdatedTableFormat(model, queryToken, action.encoding, snapshot.fieldFormats, snapshot.format),
          };
        }

        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateEncoding.type],
        });
      })
    )
  );

  // Update color palette token
  updateColors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateColors, ChartActions.updateDesignerColors, ChartActions.updateExplorationColors),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.accountFacade.reportOwnerColorPalettes$,
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs)
      ),
      switchMap(([{ action, token, snapshot }, palettes, planRefs]) => {
        let refId: string;
        if (action.type === ChartActions.updateDesignerColors.type) {
          refId = FLAMINGO_RESERVED_REFS.ChartDesigner;
        } else if (action.type === ChartActions.updateExplorationColors.type) {
          refId = FLAMINGO_RESERVED_REFS.Exploration;
        } else {
          refId = token;
        }

        return this.store
          .select(
            fromChartSelectors.createRenderDataModelSelector(
              token,
              // Chart designer has a separate plan due report charts being affected by report filters
              planRefs[refId]
            )
          )
          .pipe(
            first(),
            map((renderModel) => ({
              action,
              token,
              snapshot,
              palettes,
              renderModel,
            }))
          );
      }),
      map(({ action, token, snapshot, palettes, renderModel }) => {
        const newFormat = produce(snapshot.format, (format) => {
          format.colors.custom = action.palette;

          if (action.colorMap) {
            format.colors.map = action.colorMap;
          } else if (renderModel != null) {
            // if we're in the middle of querying, or if it failed, then don't make a map
            // the on-the-fly mapping should cover this case
            format.colors.map = generateColorMapping(renderModel, snapshot.encoding, format);
          }
        });

        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format);
        const fieldColors = useMuzeRenderer
          ? // Regenerate a new color mapping since the palette length may have changed.
            produce(snapshot.fieldColors, (colors) => {
              Object.keys(colors).forEach((key) => {
                colors[key].colorPaletteToken = action.colorPaletteToken;
                colors[key].palette = action.palette;
                colors[key].map = Object.keys(colors[key].map).reduce((records, value, index) => {
                  records[value] = index % action.palette.length;
                  return records;
                }, {} as Record<string, number>);
              });
            })
          : snapshot.fieldColors;

        const palette = palettes.find((p) => p.token === action.colorPaletteToken);
        if (action.showAlert) {
          this.alertService.success(palette.name + ' applied.');
        }

        return ChartActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            format: newFormat,
            colorPaletteToken: action.colorPaletteToken,
            fieldColors,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateExplorationColors.type],
        });
      })
    )
  );

  /**
   * backfill/apply color mapping
   */
  executeBackfillColorMapping$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FlamingoActions.planSuccess),
      withLatestFrom(
        this.store.select(fromChartSelectors.selectedToken),
        this.dataViewFacade.model$,
        (action, selectedVizToken, model) => ({
          // This handles backfill for designers and report charts, but plans are run on everything and
          // so the refId needs to be mapped appropriately
          token: action.refId !== FLAMINGO_RESERVED_REFS.ChartDesigner ? action.refId : selectedVizToken, // TODO: expand to other designers
          action,
          model,
        })
      ),
      switchMap(
        ({ token }) =>
          combineLatest(
            this.store.selectNoEmpty(fromChartSelectors.createChartSnapshotSelector(token)),
            this.store.selectNoEmpty(fromChartSelectors.createQueryTokenSelector(token))
          ).pipe(first()),
        ({ action, token, model }, [snapshot, queryToken]) => ({
          action,
          token,
          model,
          snapshot,
          queryToken,
        })
      ),
      filter(({ snapshot }) => snapshot.chartType !== 'table'),
      map(({ action, token, model, snapshot, queryToken }) => {
        // This updates the formats to fill in value->color mappings. This
        // happens after the visualization has been persisted. The changes
        // here are delivered as diffs to the iframe when report viewing.
        //
        // Add color mappings for values which aren't already mapped.
        // We want to do the minimum possible here. We are only mapping
        // for QuickCharts to maintain functionality. We shouldn't
        // do anything for VizExplorer.
        //
        // QuickCharts rendered with HighCharts:
        //    - Update 'format' with new colors only up to limit
        //    - Leave 'fieldColors' alone
        // QuickCharts rendered with Muze
        //    - Leave the colors in the 'format' color map alone
        //    - Build/Update a 'fieldColors' based on the current color field
        // VizExplorer chart
        //    - Leave the colors in the 'format' color map alone
        //    - Leave 'fieldColors' alone.
        //
        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format);

        const renderModel = SwitchRenderModel.make(model, queryToken, action.request, action.result);
        const format =
          snapshot.format == null
            ? snapshot.format
            : produce(snapshot.format, (draft) => {
                // The 'format' colors are only used by HighCharts.
                // Don't set otherwise, just leave it alone. Clearing it can cause a large diff in ReportViewer
                if (!useMuzeRenderer) {
                  // Do on the fly backfill first, in case it's necessary
                  draft.colors.map = flamingoBackfillOnTheFlyColoring(
                    draft,
                    renderModel,
                    snapshot.encoding,
                    action.request,
                    action.result
                  );

                  // In QC, the ExprFrame is not set on analytic fields. We need to ensure the default
                  // frame is set on all fields in the encoding so that the series names are generated
                  // properly.
                  const encodingWithExprFrames = EncodingHelpers.setMissingAnalyticFrames(
                    snapshot.encoding,
                    renderModel
                  );
                  const newSeriesNames = renderModel?.getColorDomain(encodingWithExprFrames, snapshot.format) ?? [];
                  draft.colors.map = updateChartLevelColoring(draft, newSeriesNames, snapshot.encoding);
                }
              });
        // For Muze rendered QuickChart, build a new 'fieldColors' map based on the current color field.
        // This will merge the color assignments if the field is already defined. Any non-color fields
        // are removed from the map.
        const isMuzeQuickChart = useMuzeRenderer && !ChartTypes.isVegasChartType(snapshot.chartType);
        const fieldColors = isMuzeQuickChart
          ? updateChartFieldColorMap(
              renderModel,
              snapshot.encoding,
              snapshot.format,
              snapshot.fieldColors,
              snapshot.colorPaletteToken
            )
          : snapshot.fieldColors;

        return ChartActions.updateChartNoEncodingTemp({
          token,
          chart: {
            ...snapshot,
            format,
            fieldColors,
          },
          description: null,
          noUndo: true,
        });
      })
    )
  );

  updateFieldColor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ChartActions.updateFieldColor,
        ChartActions.updateDesignerFieldColor,
        ChartActions.updateExplorationFieldColor
      ),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.accountFacade.reportOwnerColorPalettes$,
        this.store.select(fromFlamingoSelectors.getCompletedPlanRefs)
      ),
      switchMap(([{ action, token, snapshot }, palettes, planRefs]) => {
        // Chart designer has a separate plan due report charts being affected by report filters
        const refId =
          action.type === ChartActions.updateDesignerFieldColor.type ? FLAMINGO_RESERVED_REFS.ChartDesigner : token;

        return this.store.select(ChartSelectors.createRenderDataModelSelector(token, planRefs[refId])).pipe(
          first(),
          map((renderModel) => ({
            action,
            token,
            snapshot,
            palettes,
            renderModel,
          }))
        );
      }),
      map(({ action, token, snapshot, palettes, renderModel }) => {
        const fieldColors = produce(snapshot.fieldColors || {}, (draft) => {
          const colorMap =
            action.colorMap ||
            (renderModel
              ? generateSeriesColorMapping(
                  renderModel,
                  snapshot.encoding,
                  snapshot.format,
                  action.palette,
                  action.series
                )
              : {});
          draft[getColorMapKey(action.formula, action.varType)] = {
            colorPaletteToken: action.colorPaletteToken,
            palette: action.palette,
            map: colorMap,
            gradient: action.gradient,
            unassignedColor: action.unassignedColor,
          };
        });
        const encoding =
          action.colorTarget !== undefined
            ? EncodingHelpers.updateMarkColorTarget(snapshot.encoding, action.colorTarget).encoding
            : snapshot.encoding;
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            fieldColors,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateFieldColor.type],
        });
      })
    )
  );

  // Updates facet size map. LEGACY
  updateFacetSizes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateFacetSizes),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return ChartActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            facetSizes: { ...(snapshot.facetSizes ?? {}), ...(action.facetSizes ?? {}) },
          },
          description: 'update facet size mapping',
        });
      })
    )
  );

  // Updates chart encoding and format in flat table. LEGACY
  updateEncodingAndFormatInFlatTable$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateEncodingAndFormat),
      withLatestSnapshot(this.store, 'token'),
      filter(({ snapshot }) => snapshot.chartType === 'table'),
      map(({ action, token, snapshot }) => {
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding,
            format: { ...snapshot.format, ...(action.format ?? {}) },
            fieldColors: action.fieldColors ?? snapshot.fieldColors,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateEncodingAndFormat.type],
        });
      })
    )
  );

  replaceChartHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.replaceChartHistory),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, snapshot, token }) => {
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding: action.encoding ?? snapshot.encoding,
            isAutoUpdateDisabled: action.isAutoUpdateDisabled ?? snapshot.isAutoUpdateDisabled,
          },
          description: 'replace last encoding',
        });
      })
    )
  );

  // Change Hierarchy
  changeHierarchy$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.changeHierarchy),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      filter(([{ action, snapshot, queryToken }, model]) => {
        const canChange = EncodingHelpers.canChangeHierarchy(snapshot.encoding, model, queryToken);
        return action.forward ? canChange.forward : canChange.back;
      }),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const encoding = EncodingHelpers.changeHierarchy(
          snapshot.encoding,
          Switch.Views.find(model, queryToken),
          action.forward
        ).encoding;
        return ChartActions.updateChart({
          token,
          description: 'change hierarchy',
          chart: {
            ...snapshot,
            encoding,
          },
        });
      })
    )
  );

  // Drill Down
  drillDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.drilldown),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const schemaModel = SwitchSchemaModel.make(model, queryToken);
        const chartType = ChartDrillDown.newChartType(snapshot.chartType, action.target);
        const encoding = ChartDrillDown.encode(schemaModel, snapshot.encoding, action.target).encoding;
        return ChartActions.updateChart({
          token,
          description: 'drilldown',
          chart: {
            ...snapshot,
            encoding,
            chartType,
          },
        });
      })
    )
  );

  dynamicDrillDown$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.dynamicDrillDown),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, snapshot, token, queryToken }, model]) => {
        const modeView = Switch.Views.find(model, queryToken);
        if (!modeView) {
          throw new Error(`ModeView is invalid for queryToken: ${queryToken}`);
        }
        const encoding = ChartDrillDown.updateEncodingForDynamicDrillDown(
          model,
          modeView,
          snapshot.encoding,
          action.target,
          action.targetFieldId,
          action.drillToField,
          action.dateTruncUnit
        );
        return ChartActions.updateChart({
          token,
          description: 'dynamic-drill-down',
          chart: {
            ...snapshot,
            encoding,
          },
        });
      })
    )
  );

  updateBigValueDescription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateBigValueDescription),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) =>
        ChartActions.updateChart({
          token,
          description: 'update big value description',
          chart: {
            ...snapshot,
            format: {
              ...snapshot.format,
              bigValue: {
                ...snapshot.format.bigValue,
                description: action.description,
              },
            },
          },
        })
      )
    )
  );

  updateCaption$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateCaption),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) =>
        ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            caption: action.caption,
          },
          description: 'update updateCaption',
        })
      )
    )
  );

  // Update IsAutoUpdateDisabled
  updateIsAutoUpdateDisabled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateIsAutoUpdateDisabled),
      withLatestSnapshot(this.store, 'token'),
      map(({ action, token, snapshot }) =>
        ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            isAutoUpdateDisabled: action.isAutoUpdateDisabled,
          },
          description: 'update isAutoUpdateDisabled',
        })
      )
    )
  );

  // Sorts
  removeSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.removeSort),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const encoding = EncodingHelpers.removeSort(snapshot.encoding, action.formula).encoding;

        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.removeSort.type],
        });
      })
    )
  );

  quickSort$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.quickSort),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const encoding = EncodingHelpers.quickSort(snapshot.encoding, action.descending, snapshot.chartType).encoding;
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.quickSort.type],
        });
      })
    )
  );

  // Clear Functions
  clearChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.clearChart),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.accountFacade.defaultCategoricalChartFormat$),
      map(([{ token, action, snapshot }, format]) => {
        const encoding = EncodingHelpers.initEncoding(snapshot.chartType).encoding;
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            colorPaletteToken: format.colors.theme,
            encoding,
            facetSizes: {},
            fieldFormats: {},
            fieldColors: {},
            format: {
              ...format,
              useMuzeRenderer: ChartFormatHelpers.isHighChartTypeEnabledForMuze(snapshot.chartType),
            },
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.clearChart.type],
        });
      })
    )
  );

  clearFilters$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.clearFilters),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const encoding = EncodingHelpers.clearFilters(snapshot.encoding).encoding;
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.clearFilters.type],
        });
      })
    )
  );

  clearSorts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.clearSorts),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const encoding = EncodingHelpers.clearSorts(snapshot.encoding).encoding;
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.clearSorts.type],
        });
      })
    )
  );

  clearFormatting$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.clearFormatting),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ action, token, snapshot, queryToken }, model]) => {
        let isMuzeQuickChart = false;
        const isTable = snapshot.chartType === 'table';
        const vizExpFormat: Pick<ChartTypes.ViewVegas, 'fieldFormats'> = { fieldFormats: {} };
        // For QC, preserve the current renderer. This shouldn't change when the formats are cleared.
        const quickChartFormat = {
          format: { ...DEFAULT_CHART_FORMAT, useMuzeRenderer: snapshot.format.useMuzeRenderer },
        };
        quickChartFormat.format.colors = snapshot.format.colors;
        if (isTable) {
          vizExpFormat.fieldFormats = deepCopy(snapshot.fieldFormats);
          quickChartFormat.format.flatTable = {
            ...DEFAULT_CHART_FORMAT.flatTable,
            columns: snapshot.format.flatTable.columns,
          };
          Object.keys(vizExpFormat.fieldFormats).forEach((key: string) => {
            const colType = vizExpFormat.fieldFormats[key].paneFormat.default?.colType;
            if (colType) {
              vizExpFormat.fieldFormats[key] = getDefaultFieldFormat(colType);
            }
          });
        }
        if (
          !ChartTypes.isVegasChartType(snapshot.chartType) &&
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, quickChartFormat.format)
        ) {
          isMuzeQuickChart = true;
          // For QuickCharts that are using the Muze renderer, we might need to update the
          // encoding depending on the nature of the format change.
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (
            schemaModel &&
            ChartTranslator.formatRequiresEncodingUpdate(
              snapshot.chartType,
              snapshot.format,
              quickChartFormat.format,
              schemaModel
            )
          ) {
            return ChartActions.updateEncoding({
              token,
              encoding: snapshot.encoding,
              format: quickChartFormat.format,
            });
          }
        }

        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            // big value chart on quickcharts uses the fieldFormats obj to store the formats
            ...(ChartTypes.isVegasChartType(snapshot.chartType) || snapshot.chartType === 'bigValue'
              ? vizExpFormat
              : quickChartFormat),
            // when you assign format to the columns using the toolbar on quickcharts it saves it on the fieldFormats obj
            ...((isMuzeQuickChart || isTable) && vizExpFormat),
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.clearFormatting.type],
        });
      })
    )
  );

  swapAxes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.swapAxes),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const chartType = EncodingHelpers.swapAxesChartType(snapshot.chartType);
        const encoding = EncodingHelpers.swapAxes(snapshot.encoding);
        return ChartActions.updateChart({
          token,
          chart: {
            ...snapshot,
            encoding,
            chartType,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.swapAxes.type],
        });
      })
    )
  );

  // Update chart formatting
  updateFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateFormat),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(this.dataViewFacade.model$),
      map(([{ token, action, snapshot, queryToken }, model]) => {
        // This effect is only called for Quick Charts so we don't have to check if the chartType is 'vegas'
        const useMuzeRenderer = ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, action.format);
        const rendererChanged =
          ChartFormatHelpers.useMuzeRenderer(snapshot.chartType, snapshot.format) !== useMuzeRenderer;
        const formatRequiresEncodingUpdate = () => {
          if (!useMuzeRenderer) {
            return false;
          }
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Schema model was not defined');
          }
          return ChartTranslator.formatRequiresEncodingUpdate(
            snapshot.chartType,
            snapshot.format,
            action.format,
            schemaModel
          );
        };
        // We need to drive an encoding update if:
        //   - the renderer changes since HighCharts and Muze may require slightly different
        //     encodings. Changing the renderer is done exclusively through the format panel.
        //   - this format change requires an encoding update. This is usually associated with
        //     text labels.
        if (rendererChanged || formatRequiresEncodingUpdate()) {
          return ChartActions.updateEncoding({
            token,
            encoding: snapshot.encoding,
            format: action.format,
          });
        }
        // Otherwise, we just need to update either the format (HighCharts) or fieldFormats (Muze).
        const convertFormats = () => {
          const schemaModel = SwitchSchemaModel.make(model, queryToken as string);
          if (!schemaModel) {
            throw new Error('Schema model was not defined');
          }
          return ChartFormatHelpers.convertFormats(schemaModel, snapshot.encoding, action.format, snapshot.chartType);
        };
        const fieldFormats = useMuzeRenderer ? convertFormats() : snapshot.fieldFormats;
        return ChartActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            format: action.format,
            fieldFormats,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateFormat.type],
        });
      })
    )
  );

  updateFieldFormat$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateFieldFormat),
      withLatestSnapshot(this.store, 'token'),
      map(({ token, action, snapshot }) => {
        const fieldFormats = produce(snapshot.fieldFormats || {}, (draft) => {
          const fieldFormat = (draft[action.formula] ||= FieldFormatHelpers.getDefaultFieldFormat());
          fieldFormat.paneFormat = {
            ...fieldFormat.paneFormat,
            [action.pane]: {
              ...action.format,
            },
          };
        });

        return ChartActions.updateChartNoEncoding({
          token,
          chart: {
            ...snapshot,
            fieldFormats,
          },
          description: EffectHelpers.actionToDescriptionMap[ChartActions.updateFieldFormat.type],
        });
      })
    )
  );

  undo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.undo),
      withLatestFrom(
        this.store.select(fromChartSelectors.selectResources),
        this.store.select(fromChartSelectors.selectHistories),
        (action, resources, histories) => ({
          action,
          resource: resources.get(action.token),
          history: histories.get(action.token),
        })
      ),
      filter(({ resource }) => resource != null), // lightbox explorer should have no resource
      switchMap(({ action, history, resource }) =>
        this.saveChartWithSnapshot(action.snapshot, resource)
          .then((resource) => ChartActions.updateChartSuccess({ resource }))
          .catch((err) => ChartActions.redoTemp({ token: action.token, snapshot: peekRedoStack(history) }))
      )
    )
  );

  redo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.redo),
      withLatestFrom(
        this.store.select(fromChartSelectors.selectResources),
        this.store.select(fromChartSelectors.selectHistories),
        (action, resources, histories) => ({
          action,
          resource: resources.get(action.token),
          history: histories.get(action.token),
        })
      ),
      filter(({ resource }) => resource != null), // lightbox explorer should have no resource
      switchMap(({ action, history, resource }) =>
        this.saveChartWithSnapshot(action.snapshot, resource)
          .then((resource) => ChartActions.updateChartSuccess({ resource }))
          .catch((err) => ChartActions.undoTemp({ token: action.token, snapshot: peekUndoStack(history) }))
      )
    )
  );

  updateUnappliedChangesOnChartChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(...EffectHelpers.updateEncodingActions, ...EffectHelpers.updateFormatOrColorActions),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.store.select(fromChartSelectors.selectManuallyApplyChanges),
        this.store.select(fromChartSelectors.selectUnappliedChanges),
        ({ action, snapshot }, manuallyApplyChanges, unappliedChanges) => ({
          action,
          snapshot,
          manuallyApplyChanges: manuallyApplyChanges?.get(action.token) ?? false,
          unappliedChanges: unappliedChanges?.get(action.token) ?? 0,
        })
      ),
      filter(({ snapshot, manuallyApplyChanges }) => snapshot.isAutoUpdateDisabled && !manuallyApplyChanges),
      map(({ action, unappliedChanges }) => {
        // A format change only increments the count if there has already been an encoding change. This allows the
        // user to make format changes live in Manual mode. If we find that there are some format changes that are
        // expensive, we may want to change this behavior.
        //
        // The unappliedChanges count can be positive or negative. It can be negative if the changes were incurred
        // by undo (which always decrements). EX: If the user applies the changes and then hits undo, the
        // unapplied changes will be -1 (the UI always sees the ABS). If the user makes a regular change,
        // then we want the unapplied changes to be -2 (not 0) since they are not back to the original state.
        const unapplied =
          unappliedChanges === 0 && EffectHelpers.isUpdateFormatOrColorAction(action.type)
            ? 0
            : unappliedChanges + Math.sign(unappliedChanges) || 1;
        return ChartActions.setUnappliedChangesCount({
          token: action.token,
          count: unapplied,
        });
      })
    )
  );

  updateUnappliedChangesOnUndoRedo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.undo, ChartActions.undoTemp, ChartActions.redo, ChartActions.redoTemp),
      withLatestSnapshot(this.store, 'token'),
      withLatestFrom(
        this.store.select(fromChartSelectors.selectManuallyApplyChanges),
        this.store.select(fromChartSelectors.selectUnappliedChanges),
        this.store.select(fromChartSelectors.selectHistories),
        ({ action, snapshot }, manuallyApplyChanges, unappliedChanges, history) => ({
          action,
          snapshot,
          manuallyApplyChanges: manuallyApplyChanges?.get(action.token) ?? false,
          unappliedChanges: unappliedChanges?.get(action.token) ?? 0,
          lastAction:
            action.type === ChartActions.undo.type || action.type === ChartActions.undoTemp.type
              ? history.get(action.token)?.redoStack[0]?.action
              : history.get(action.token)?.undoStack[0]?.action,
        })
      ),
      filter(
        ({ lastAction }) =>
          EffectHelpers.isUpdateChartAction(lastAction) || lastAction === ChartActions.updateIsAutoUpdateDisabled.type
      ),
      map(({ action, snapshot, manuallyApplyChanges, unappliedChanges, lastAction }) => {
        const isFormatOrColorChange = EffectHelpers.isUpdateFormatOrColorAction(lastAction);
        const isUndo = action.type === ChartActions.undo.type || action.type === ChartActions.undoTemp.type;
        const unappliedChangeCount = (() => {
          if (!snapshot.isAutoUpdateDisabled || manuallyApplyChanges) {
            // In auto-update mode or when the user manually applied the changes,
            // the unapplied count should be reset to 0.
            return 0;
          }
          if (isFormatOrColorChange && unappliedChanges === 0) {
            // When the change is format/color related and we are back to 0 changes,
            // don't change the count.
            return unappliedChanges;
          }
          // Otherwise, increment/decrement based on the type
          return isUndo ? --unappliedChanges : ++unappliedChanges;
        })();

        return ChartActions.setUnappliedChangesCount({
          token: action.token,
          count: unappliedChangeCount,
        });
      })
    )
  );
  // Persist chart changes
  updateChart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateChart, ChartActions.updateChartNoEncoding),
      withLatestFrom(this.store.select(fromChartSelectors.selectResources), (action, resources) => ({
        action,
        resource: resources.get(action.token),
      })),
      filter(({ resource }) => resource != null), // lightbox explorer should have no resource
      switchMap(({ action, resource }) =>
        this.saveChartWithSnapshot(action.chart, resource)
          .then((resource) => ChartActions.updateChartSuccess({ resource }))
          .catch((err) => ChartActions.updateChartFailure({ token: action.token, error: err }))
      )
    )
  );

  // Fire undo on updateChartFailure
  updateFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ChartActions.updateChartFailure),
      withLatestFrom(this.store.select(fromChartSelectors.selectHistories), (action, histories) => ({
        action,
        history: histories.get(action.token),
      })),
      tap(({ action }) => {
        // TODO: This is temporary, and can be removed when the corresponding code in `updateChart` is removed.
        // Extra check for the specific error type to display a slightly different error
        // notification in this case, since the issue isn't a lack of connection.
        if (action.error instanceof MismatchedApiError) {
          this.alertService.error(
            'There was a problem saving your changes.<br/>Please reload the browser and try again.'
          );
        } else {
          this.alertService.error(
            'There was a problem saving your changes.<br/>Please check your internet connection and try again.'
          );
        }
      }),
      map(({ action, history }) => ChartActions.undoTemp({ token: action.token, snapshot: peekUndoStack(history) }))
    )
  );
}
